import { useState } from "react";
import {
	DEFAULT_HOUSE_ABOVE_CEILING_M,
	HOUSE_ABOVE_CEILING_LIMITS,
	INDOOR_WALLS,
	type IndoorRoomConfig,
	type IndoorWall,
} from "../../domain/indoor/indoorRoom";
import { useT } from "../../i18n";

interface IndoorRoomEditorProps {
	indoorRoom: IndoorRoomConfig;
	onIndoorRoomChange: (indoorRoom: IndoorRoomConfig) => void;
}

const WALL_LABEL_KEYS = {
	front: "openings_wallFront",
	back: "openings_wallBack",
	left: "openings_wallLeft",
	right: "openings_wallRight",
} as const satisfies Record<IndoorWall, string>;

/**
 * Inomhus (issues 174/175): the room the sauna is built into. Which walls
 * stand against the room's own walls, and how much house is above the sauna
 * ceiling for the chimney.
 */
export function IndoorRoomEditor({
	indoorRoom,
	onIndoorRoomChange,
}: IndoorRoomEditorProps) {
	const t = useT();
	// Typed text is kept locally and committed only when it is a height in
	// range (or on blur): the store clamps, so committing "0" on the way to
	// "0.8" would snap the field to 0.5 under the user's cursor.
	const committed =
		indoorRoom.houseAboveCeilingM === null
			? ""
			: String(indoorRoom.houseAboveCeilingM);
	const [draft, setDraft] = useState(committed);
	// Re-sync when the store changes under us (undo, load, clamp): React's
	// "adjust state on prop change" pattern, not an effect.
	const [syncedFrom, setSyncedFrom] = useState(committed);
	if (syncedFrom !== committed) {
		setSyncedFrom(committed);
		setDraft(committed);
	}
	const commitHeight = (raw: string, force: boolean) => {
		const value = Number(raw.replace(",", "."));
		if (raw.trim() === "") {
			onIndoorRoomChange({ ...indoorRoom, houseAboveCeilingM: null });
			return;
		}
		if (!Number.isFinite(value)) {
			if (force) setDraft(committed);
			return;
		}
		const inRange =
			value >= HOUSE_ABOVE_CEILING_LIMITS.min &&
			value <= HOUSE_ABOVE_CEILING_LIMITS.max;
		if (inRange || force) {
			onIndoorRoomChange({ ...indoorRoom, houseAboveCeilingM: value });
		}
	};
	const toggleWall = (wall: IndoorWall, checked: boolean) => {
		const walls = new Set(indoorRoom.existingWalls);
		if (checked) walls.add(wall);
		else walls.delete(wall);
		onIndoorRoomChange({
			...indoorRoom,
			existingWalls: INDOOR_WALLS.filter((w) => walls.has(w)),
		});
	};

	return (
		<div className="inspector-section-body">
			<div className="builder-controls indoor-room-editor">
				<fieldset className="indoor-room-walls">
					<legend className="section-label">
						{t.indoor_existingWallsLabel}
					</legend>
					<div className="indoor-room-walls__grid">
						{INDOOR_WALLS.map((wall) => (
							<label key={wall} className="thermal-checkbox">
								<input
									type="checkbox"
									checked={indoorRoom.existingWalls.includes(wall)}
									onChange={(event) => toggleWall(wall, event.target.checked)}
								/>
								{t[WALL_LABEL_KEYS[wall]]}
							</label>
						))}
					</div>
				</fieldset>
				<p className="site-conditions-note">{t.indoor_existingWallsHint}</p>

				<div className="input-row">
					<label htmlFor="indoor-house-above">
						{t.indoor_houseAboveLabel} (m)
					</label>
					<input
						id="indoor-house-above"
						type="text"
						inputMode="decimal"
						placeholder={t.indoor_houseAboveUnset.replace(
							"{m}",
							String(DEFAULT_HOUSE_ABOVE_CEILING_M),
						)}
						value={draft}
						onChange={(event) => {
							setDraft(event.target.value);
							commitHeight(event.target.value, false);
						}}
						onBlur={(event) => {
							commitHeight(event.target.value, true);
							// The store may have clamped or rounded it.
							setDraft(committed);
						}}
					/>
				</div>
				<p className="site-conditions-note">{t.indoor_houseAboveHint}</p>
			</div>
		</div>
	);
}
